import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send as _lr_Send, Bot as _lr_Bot, User as _lr_User, Sparkles as _lr_Sparkles, RefreshCw as _lr_RefreshCw } from 'lucide-react';
import Button from './ui/Button';

const Send = _lr_Send || (() => null);
const Bot = _lr_Bot || (() => null);
const User = _lr_User || (() => null);
const Sparkles = _lr_Sparkles || (() => null);
const RefreshCw = _lr_RefreshCw || (() => null);
const INITIAL = [
  { role: 'bot', text: "👋 Hi! I'm HireBot, your AI recruiting assistant. I can help you screen candidates, draft job descriptions, analyze pipeline health, or answer questions about your candidates. What would you like to do today?" },
];

const QUICK = [
  'Screen a candidate for React skills',
  'Draft a job description for ML Engineer',
  'Summarize pipeline health',
  'Who are my top candidates this week?',
];

const RESPONSES = {
  default: [
    "Based on your current pipeline, you have **10 active candidates** across 5 stages. Your top performers are Carlos Rivera (96% match) and James Wilson (95% match) — both in late stages.",
    "I\'ve analyzed your job requirements and found 3 strong matches in the Applied stage. Would you like me to auto-schedule screening calls for them?",
    "Your pipeline conversion rate is **68%** from Screening to Shortlisted — above industry average of 52%. Great work! The Interview → Offer stage could use attention (currently 42%).",
    "I can initiate AI screening calls for any candidate. These 10-minute calls assess technical skills, communication, and culture fit, then generate a structured scorecard automatically.",
  ],
  react: "For React screening, I assess: **component architecture**, **state management** (Redux/Zustand/Context), **performance optimization** (memoization, lazy loading), **TypeScript proficiency**, and **testing** (Jest/RTL). Want me to start a screening call with a specific candidate?",
  jd: "Here\'s a draft JD for **ML Engineer**:\n\n**Role:** ML Engineer\n**Salary:** $155K–$195K | Remote\n\n**Requirements:**\n• 4+ years ML/AI experience\n• Proficiency in PyTorch or TensorFlow\n• MLOps pipeline experience (MLflow, Kubeflow)\n• Strong Python skills\n• Experience deploying models to production\n\nShall I refine this further?",
  pipeline: "**Pipeline Health Summary:**\n\n• Applied: 3 candidates (avg score 74%)\n• Screening: 2 candidates (avg score 87%)\n• Shortlisted: 2 candidates (avg score 93%)\n• Interview: 2 candidates (avg score 87%)\n• Offer: 2 candidates (avg score 92%)\n\n⚡ Recommendation: Move Liam Chen and Carlos Rivera to Interview stage — both scored 90%+.",
  top: "**Top Candidates This Week:**\n\n1. 🥇 Carlos Rivera — 96% match (Security Engineer)\n2. 🥈 James Wilson — 95% match (Backend Engineer)\n3. 🥉 Priya Sharma — 92% match (Product Designer)\n4. Liam Chen — 91% match (DevOps Engineer)\n5. Yuki Tanaka — 90% match (Frontend Developer)\n\nAll 5 are recommended for fast-track processing.",
};

function getBotReply(text) {
  const t = text.toLowerCase();
  if (t.includes('react') || t.includes('screen')) return RESPONSES.react;
  if (t.includes('jd') || t.includes('job description') || t.includes('ml engineer')) return RESPONSES.jd;
  if (t.includes('pipeline') || t.includes('health') || t.includes('summar')) return RESPONSES.pipeline;
  if (t.includes('top') || t.includes('best') || t.includes('this week')) return RESPONSES.top;
  const arr = RESPONSES.default;
  return arr[Math.floor(Math.random() * arr.length)];
}

export function ChatPanel() {
  const [messages, setMessages] = useState(INITIAL);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, typing]);

  const send = (text) => {
    const msg = text || input.trim();
    if (!msg) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, { role: 'bot', text: getBotReply(msg) }]);
    }, 1200 + Math.random() * 600);
  };

  const reset = () => { setMessages(INITIAL); setInput(''); setTyping(false); };

  return (
    <motion.div initial={{ opacity: 1, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" style={{ height: '70vh' }}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-white">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
            <Bot size={16} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">HireBot AI</p>
            <p className="text-xs text-emerald-600 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block" />Online</p>
          </div>
        </div>
        <button onClick={reset} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"><RefreshCw size={15} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <AnimatePresence>
          {messages.map((msg, i) => (
            <motion.div key={i} initial={{ opacity: 1, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'bot' && (
                <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles size={13} className="text-purple-600" />
                </div>
              )}
              <div className={`max-w-sm px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                msg.role === 'user'
                  ? 'bg-purple-600 text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
              }`}>
                {msg.text.replace(/\*\*(.*?)\*\*/g, '$1')}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                  <User size={13} className="text-gray-600" />
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        {typing && (
          <motion.div initial={{ opacity: 1 }} animate={{ opacity: 1 }} className="flex gap-2 items-center">
            <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center">
              <Sparkles size={13} className="text-purple-600" />
            </div>
            <div className="bg-gray-100 px-4 py-2.5 rounded-2xl rounded-bl-sm flex gap-1">
              {[0,1,2].map(d => <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}
            </div>
          </motion.div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="px-4 pb-2 flex flex-wrap gap-1.5">
        {QUICK.map(q => (
          <button key={q} onClick={() => send(q)}
            className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-3 py-1 rounded-full hover:bg-purple-100 transition-colors">
            {q}
          </button>
        ))}
      </div>

      <div className="p-3 border-t border-gray-100">
        <div className="flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
            placeholder="Ask HireBot anything about your pipeline…"
            className="flex-1 px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400" />
          <motion.button whileTap={{ scale: 0.95 }} onClick={() => send()}
            className="w-10 h-10 rounded-xl bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center transition-colors">
            <Send size={16} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

export default ChatPanel;